import { Button, Input, useToast } from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import Draggable from "react-draggable"
import { EditorSteps, Position } from "../editor-utils/editor-types"
import { StripLastSegment, TruncateString } from "../editor-utils/id-string-utils"
import { ResetSelectedPath } from "../helpers/svg-helpers"
import { GetLinerLocationInfo } from "../helpers/wear-reference-plane-helper"
import asEditorStore from "../local-store/asset-section-editor-store"
import { ProcessSubmitEditBox } from "./processses-edit-box-submit"
import { LinerLocation } from "features/organizations/asset-sections/models/asset-section-models"

interface EditSvgBoxComponent {
    assetSectionId: string
    linerLocations: LinerLocation[]
    boxPosition: Position
    svgCurrentPosition: Position
}

interface HoleLocationEditBoxProps {
    title: string
    inputNameValue: string
    onInputNameValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onCancel: () => void
    onSubmit: () => void
    leftPosition: number
    topPosition: number
}

export const EditSvgBoxComponent: React.FC<EditSvgBoxComponent> = observer(({ assetSectionId, linerLocations, boxPosition, svgCurrentPosition }) => {
    const { setMessage } = useToast()
    const { currentStep, editBoxInputValue } = asEditorStore

    // Early return for SVGSettings step
    if (currentStep === EditorSteps.SVGSettings) return null

    // Shared variables across different cases
    const pathId = StripLastSegment(asEditorStore.selectedPathId as string)
    const charLong = currentStep === EditorSteps.LinersLocation ? 12 : 22
    const linerLocationInfo = GetLinerLocationInfo(linerLocations, pathId)
    const truncatePathId = TruncateString(pathId, charLong)

    // Render function for content based on currentStep
    const renderEditBoxContent = () => {
        switch (currentStep) {
            case EditorSteps.HolesLocation:
                return {
                    title: `Hole Id: ${truncatePathId}`,
                    inputType: "text",
                    inputValue: editBoxInputValue,
                    onInputChange: handleEditBoxInputChange,
                    onCancel: onEditBoxCancle,
                }

            case EditorSteps.WearReferencePlane:
                return {
                    title: `Wear Reference Plane: ${linerLocationInfo?.reference} => ${linerLocationInfo?.wallThickness}`,
                    inputType: "number",
                    inputValue: editBoxInputValue,
                    onInputChange: handleEditBoxInputChange,
                    onCancel: onEditBoxCancle,
                }

            case EditorSteps.LinersDrawingReferences:
                return {
                    title: `Liners Drawing References: ${linerLocationInfo?.drawingReference}`,
                    inputType: "text",
                    inputValue: editBoxInputValue,
                    onInputChange: handleEditBoxInputChange,
                    onCancel: onEditBoxCancle,
                }
            case EditorSteps.LinersLocation:
                return {
                    title: `Liner Location Reference: ${truncatePathId}`,
                    inputType: "text",
                    inputValue: editBoxInputValue,
                    onInputChange: handleEditBoxInputChange,
                    onCancel: onEditBoxCancle,
                }
        }
    }

    const contentProps = renderEditBoxContent()

    const onEditBoxCancle = () => {
        if (asEditorStore.selectedPathId) ResetSelectedPath(asEditorStore.selectedPathId)
        asEditorStore.setShowEditBox(false)
        asEditorStore.setSelectedPathId(null)
        asEditorStore.setEditBoxLinerRegionNameInputValue("")
    }

    const handleEditBoxInputChange = (value: string) => {
        asEditorStore.setEditBoxInputValue(value)
    }

    const leftPosition = (boxPosition?.x as number) + svgCurrentPosition.x
    const topPosition = (boxPosition?.y as number) + svgCurrentPosition.y
    if (currentStep === EditorSteps.HolesLocation)
        return (
            <SvgHoleLocationEditBox
                title={contentProps.title}
                inputNameValue={editBoxInputValue as string}
                leftPosition={leftPosition}
                topPosition={topPosition}
                onCancel={onEditBoxCancle}
                onSubmit={() => ProcessSubmitEditBox({ assetSectionId, setMessage })}
                onInputNameValueChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditBoxInputChange(e.target.value)}
            />
        )

    return (
        <Draggable>
            <div
                className='edit-box'
                style={{
                    left: `${leftPosition}px`,
                    top: `${topPosition}px`,
                }}
            >
                <div className='box-title'>{contentProps.title}</div>

                <div className='box-body'>
                    <Input
                        className={"reference-input"}
                        value={editBoxInputValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditBoxInputChange(e.target.value)}
                        type={contentProps.inputType}
                    />

                    <Button onClick={onEditBoxCancle} secondary>
                        Cancel
                    </Button>
                    <Button onClick={() => ProcessSubmitEditBox({ assetSectionId, setMessage })} primary>
                        Submit
                    </Button>
                </div>
            </div>
        </Draggable>
    )
})

const SvgHoleLocationEditBox: React.FC<HoleLocationEditBoxProps> = observer(
    ({ title, inputNameValue, onInputNameValueChange, onCancel, onSubmit, leftPosition, topPosition }) => {
        const handleEditBoxLinerRegionNameInputChange = (value: string) => {
            asEditorStore.setEditBoxLinerRegionNameInputValue(value)
        }
        return (
            <Draggable>
                <div
                    className='edit-box'
                    style={{
                        left: `${leftPosition}px`,
                        top: `${topPosition}px`,
                    }}
                >
                    <div className='box-title' style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>{title}</span>
                        <span style={{ display: "flex", gap: "0.3em" }}>
                            <Button onClick={onCancel} secondary>
                                Cancel
                            </Button>
                            <Button onClick={onSubmit} primary>
                                Submit
                            </Button>
                        </span>
                    </div>
                    <div className='box-body' style={{ display: "flex", alignItems: "baseline", gap: "0.5em" }}>
                        <span>
                            <label>{`${asEditorStore.surroundingHoleLiner}-Region`}</label>
                            <Input className={"hole-input"} value={inputNameValue} onChange={onInputNameValueChange} type={"text"} />
                            <label>-Hole1,</label>
                        </span>
                        <span>
                            <label>{"Region Name: "}</label>
                            <Input
                                className={"liner-location-draw-ref-input"}
                                value={asEditorStore.editBoxLinerRegionNameInputValue}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditBoxLinerRegionNameInputChange(e.target.value)}
                                type={"text"}
                            />
                        </span>
                    </div>
                </div>
            </Draggable>
        )
    },
)
